import React, {FC, useEffect, useState} from 'react';
import {loadStripe, Stripe} from '@stripe/stripe-js';
import {Elements} from '@stripe/react-stripe-js';
import {Row, Col, Card, Typography, Button, Badge, Modal, Form, InputNumber, Result, message} from 'antd';
import walletService from '../../../services/walletService';
import {formatString, formatValue, useIsMobile} from '../../../utils/helpers';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store/rootReducer';
import {getCookie} from 'typescript-cookie';
import {useNavigate} from 'react-router-dom';
import DepositForm from './depositForm';

import "./style.css";

const {Paragraph, Text} = Typography;

interface IndividualUser {
    username: string;
    userType: 'individual';
    profile: { firstName: string; lastName: string };
}

interface BusinessUser {
    username: string;
    userType: 'business';
    businessInfo: { companyName: string };
}

type User = IndividualUser | BusinessUser;

interface Wallet {
    balance: number;
    currency: string;
    status: string;
    userId: User;
    roleType?: string;
}

const stripePK = process.env.REACT_APP_STRIPE_PK || '';
const stripePromise: Promise<Stripe | null> = loadStripe(stripePK);

const WalletPage: FC = () => {
    const [wallet, setWallet] = useState<Wallet>({
        balance: 0, currency: 'USD', status: '',
        userId: {username: '', userType: 'individual', profile: {firstName: '', lastName: ''}}
    });
    const currentUser: any = useSelector((s: RootState) => s.auth.user);
    const canWithdraw = Boolean(currentUser?.canWithdraw);
    const role = getCookie('roleType') || currentUser?.roles?.[0]?.roleType;
    const isFeeRole = role === 'promoter' || role === 'partner';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const isMobile = useIsMobile();

    const [showDepositModal, setShowDepositModal] = useState(false);
    const [depositAmount, setDepositAmount] = useState<number | null>(null);
    const [step, setStep] = useState<'input' | 'pay' | 'result'>('input');
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [paymentOk, setPaymentOk] = useState(false);

    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState<number | null>(null);
    const [withdrawLoading, setWithdrawLoading] = useState(false);

    const navigate = useNavigate();

    const fetchWallet = async () => {
        setLoading(true);
        try {
            const data = await walletService.getWallet();
            setWallet(data);
        } catch {
            setError('Failed to load wallet details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWallet();
    }, []);

    useEffect(() => {
        const handleCloseAll = () => {
            setShowDepositModal(false);
            setShowWithdrawModal(false);

            setClientSecret(null);
            setStep('input');
            setDepositAmount(null);
            setWithdrawAmount(null);
        };

        window.addEventListener('close-all-modals', handleCloseAll);
        return () => window.removeEventListener('close-all-modals', handleCloseAll);
    }, []);

    const closeModals = () => {
        setShowDepositModal(false);
        setShowWithdrawModal(false);

        setClientSecret(null);
        setStep('input');
        setDepositAmount(null);
        setWithdrawAmount(null);
    };

    const openDeposit = () => {
        setDepositAmount(null);
        setClientSecret(null);
        setStep('input');
        setShowDepositModal(true);
    };

    const onAmountSubmit = async () => {
        if (!depositAmount || depositAmount <= 0) return;

        try {
            const res = await walletService.depositFunds({amount: depositAmount});
            setClientSecret(res.clientSecret);
            setStep('pay');
        } catch (e: any) {
            message.error(e.message || 'Error creating payment intent');
            closeModals();
        }
    };

    const openWithdraw = () => setShowWithdrawModal(true);

    const onWithdrawSubmit = async (values: { amount: number }) => {
        setWithdrawLoading(true);
        try {
            await walletService.withdrawFunds({amount: values.amount});
            message.success('Withdrawal submitted. Track transfer and bank payout status in Transactions.');
            closeModals();
            fetchWallet();

            setTimeout(() => {
                fetchWallet();
            }, 1500);
        } catch (e: any) {
            message.error(e.message || 'Failed to request withdrawal');
        } finally {
            setWithdrawLoading(false);
        }
    };

    return (
        <div style={{width: '100%', maxWidth: 1100, margin: '0 auto', padding: 16}}>
            <label className={"title"}>My wallet</label>

            {loading && <Text>Loading…</Text>}
            {error && <Text type="danger">{error}</Text>}

            {!loading && !error && (
                <>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Card style={{wordBreak: 'break-word'}}>
                                {wallet.userId.userType === 'individual' ? (
                                    <>
                                        <Text strong>Name:</Text> {wallet.userId.profile.firstName}{' '}
                                        {wallet.userId.profile.lastName} ({wallet.userId.username})
                                    </>
                                ) : (
                                    <>
                                        <Text strong>Company:</Text> {wallet.userId.businessInfo.companyName}{' '}
                                        ({wallet.userId.username})
                                    </>
                                )}
                                <br/>
                                <Text strong>Account type:</Text> {formatString(wallet.userId.userType)}
                                <br/>
                                <Text strong>Role:</Text> {formatString(wallet.roleType || '')}
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} style={{marginTop: 20}}>
                        <Col xs={24} sm={12} md={8}>
                            <Card title="Balance">${formatValue(wallet.balance)}</Card>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                            <Card title="Currency">{wallet.currency}</Card>
                        </Col>

                        <Col xs={24} sm={12} md={8}>
                            <Card title="Status">
                                <Badge
                                    status={wallet.status === 'active' ? 'success' : 'default'}
                                    text={formatString(wallet.status)}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <div
                        className="pageActions"
                        style={{
                            display: 'flex',
                            gap: 8,
                            flexWrap: 'wrap',
                            justifyContent: isMobile ? 'stretch' : 'flex-end',
                            marginTop: 16,
                        }}
                    >
                        {role === 'advertiser' && (
                            <Button
                                type="primary"
                                onClick={openDeposit}
                                block={isMobile}
                                style={{width: isMobile ? '100%' : 'auto'}}
                            >
                                Deposit
                            </Button>
                        )}

                        <Button
                            danger
                            onClick={openWithdraw}
                            block={isMobile}
                            style={{width: isMobile ? '100%' : 'auto'}}
                        >
                            Withdraw
                        </Button>
                    </div>

                </>
            )}

            <Modal open={showDepositModal} title="Deposit Funds" onCancel={closeModals}
                   footer={null} destroyOnClose width={isMobile ? '92vw' : 520}
                   styles={{body: {padding: isMobile ? 12 : 24}}}>
                {step === 'input' && (
                    <Form layout="vertical" onFinish={onAmountSubmit}>
                        <Form.Item
                            label={`Amount (${wallet.currency})`}
                            name="amount"
                            rules={[
                                {required: true, message: 'Please enter an amount'},
                                {
                                    validator: (_, value) => value >= 20
                                        ? Promise.resolve()
                                        : Promise.reject(new Error('Minimum deposit is $20')),
                                }
                            ]}
                        >
                            <InputNumber
                                style={{width: '100%'}}
                                min={0}
                                value={depositAmount || 0}
                                onChange={(v) => setDepositAmount(v!)}
                            />
                        </Form.Item>
                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap'}}>
                            <Button
                                onClick={closeModals}
                                block={isMobile}
                                style={{width: isMobile ? '100%' : 'auto'}}
                            >
                                Cancel
                            </Button>

                            <Button
                                type="primary"
                                htmlType="submit"
                                block={isMobile}
                                style={{width: isMobile ? '100%' : 'auto'}}
                            >
                                Continue
                            </Button>
                        </div>
                    </Form>
                )}

                {step === 'pay' && clientSecret && (
                    <Elements stripe={stripePromise} options={{clientSecret}}>
                        <DepositForm
                            onSuccess={() => {
                                setPaymentOk(true);
                                setStep('result');
                            }}
                            onError={() => {
                                setPaymentOk(false);
                                setStep('result');
                            }}
                        />
                    </Elements>
                )}

                {step === 'result' &&
                    (paymentOk ? (
                        <Result
                            status="success"
                            title="Deposit successful!"
                            extra={[
                                <Button
                                    key="back"
                                    type="primary"
                                    onClick={() => {
                                        closeModals();
                                        fetchWallet();
                                        message.success('Balance updated');
                                    }}
                                >
                                    Back to Wallet
                                </Button>
                            ]}
                        />
                    ) : (
                        <Result status="error" title="Payment failed" subTitle="Please try again."
                                extra={[
                                    <Button key="retry" onClick={() => setStep('input')}>
                                        Try Again
                                    </Button>
                                ]}
                        />
                    ))}
            </Modal>

            <Modal open={showWithdrawModal} title="Withdraw Funds" onCancel={closeModals} footer={null}
                   destroyOnClose width={isMobile ? '92vw' : 520} styles={{body: {padding: isMobile ? 12 : 24}}}>
                {!canWithdraw ? (
                    <>
                        <Paragraph>
                            Your payout setup still needs attention. Please go to your profile,
                            click &#34;Get verified&#34;, and complete any identity verification or bank
                            account steps required before withdrawals.
                        </Paragraph>
                        <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                            <Button
                                type="primary"
                                block={isMobile}
                                style={{width: isMobile ? '100%' : 'auto'}}
                                onClick={() => {
                                    closeModals();
                                    navigate('/profile');
                                }}
                            >
                                Go to Profile
                            </Button>
                        </div>
                    </>
                ) : (
                    <Form layout="vertical" onFinish={onWithdrawSubmit} initialValues={{amount: null}}>
                        <Form.Item
                            label={`Amount (${wallet.currency})`}
                            name="amount"
                            rules={[
                                {required: true, message: 'Please enter an amount'},
                                {
                                    validator: (_, value) =>
                                        value > 0 && value <= wallet.balance
                                            ? Promise.resolve()
                                            : Promise.reject(
                                                new Error(
                                                    'Amount must be > 0 and no more than your balance'
                                                )
                                            ),
                                },
                            ]}
                        >
                            <InputNumber
                                style={{width: '100%'}}
                                min={0.01}
                                max={wallet.balance}
                                step={0.01}
                                disabled={withdrawLoading}
                                onChange={(value) => setWithdrawAmount(value as number)}
                            />
                        </Form.Item>

                        {withdrawAmount != null && (
                            <Paragraph type="secondary">
                                {isFeeRole
                                    ? `After 10% fee you’ll receive ${formatValue(withdrawAmount * 0.9)} ${wallet.currency}.`
                                    : `You’ll receive ${formatValue(withdrawAmount)} ${wallet.currency}.`}
                            </Paragraph>
                        )}

                        <Paragraph type="secondary">
                            Stripe standard bank payouts usually arrive in 1-4 business days after initiation.
                        </Paragraph>

                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap'}}>
                            <Button
                                onClick={closeModals}
                                disabled={withdrawLoading}
                                block={isMobile}
                                style={{width: isMobile ? '100%' : 'auto'}}
                            >
                                Cancel
                            </Button>

                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={withdrawLoading}
                                block={isMobile}
                                style={{width: isMobile ? '100%' : 'auto'}}
                            >
                                Withdraw
                            </Button>
                        </div>

                    </Form>
                )}
            </Modal>
        </div>
    );
};

export default WalletPage;
